// Nessun import: lo smoke script (node) carica questo file direttamente.

// Quante voci di una lista mostrare nel report prima di "...e altri N"
const LIST_CAP = 10

// Tetto del client sulla richiesta di ingest FTP (DS-8): molto sopra l'unica attesa di
// crescita di 5 s + il tempo di import, cosi' una richiesta che non finisce mai si
// chiude con il messaggio di timeout invece di lasciare la barra in corso all'infinito.
const INGEST_TIMEOUT_MS = 10 * 60 * 1000

const TREE_PROCESSING_LABEL = 'Invio completato: il server sta estraendo gli archivi e creando cartelle e file. '
  + 'Potrebbe volerci qualche minuto: non chiudere la finestra.'

const ROOT_MESSAGE = 'Apri la cartella in cui caricare l\'alberatura: nella radice non si possono caricare file.'

const isPlainObject = value => typeof value === 'object' && value !== null && !Array.isArray(value)

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key)

const plural = (count, singular, pluralForm) => `${count} ${count === 1 ? singular : pluralForm}`

const trimDots = text => String(text).replace(/[.\s]+$/, '')

// ---------------------------------------------------------------- tree upload

const isPositiveFolder = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0
  }
  if (typeof value === 'string') {
    return /^\d+$/.test(value) && Number(value) > 0
  }
  return false
}

const treeSelectionProblem = (files, currentFolder) => {
  if (!isPositiveFolder(currentFolder)) {
    return { blocked: true, message: ROOT_MESSAGE, offenders: [] }
  }
  if (!Array.isArray(files)) {
    return { blocked: false, message: '', offenders: [] }
  }
  const offenders = files
    .filter(file => file && typeof file.name === 'string' && !/\.zip$/i.test(file.name))
    .map(file => file.name)
  if (offenders.length > 0) {
    return { blocked: true, message: 'Sono accettati solo archivi .zip.', offenders }
  }
  return { blocked: false, message: '', offenders: [] }
}

const treeUploadSummary = payload => {
  const data = isPlainObject(payload) && isPlainObject(payload.data) ? payload.data : null
  if (!data || typeof data.folders_created !== 'number' || typeof data.files_created !== 'number') {
    return { variant: 'success', message: 'Alberatura caricata.' }
  }
  if (data.folders_created + data.files_created === 0) {
    return {
      variant: 'warning',
      message: 'Caricamento completato, ma gli archivi non contenevano file o cartelle.',
    }
  }
  return {
    variant: 'success',
    message: `Alberatura caricata: create ${plural(data.folders_created, 'cartella', 'cartelle')} e ${data.files_created} file.`,
  }
}

const failurePayload = failure => {
  if (isPlainObject(failure) && isPlainObject(failure.response) && isPlainObject(failure.response.data)) {
    return failure.response.data
  }
  if (isPlainObject(failure) && isPlainObject(failure.data)) {
    return failure.data
  }
  return null
}

const treeFailureMessage = (result, failure) => {
  const res = isPlainObject(result) ? result : {}
  if (res.status === 403) {
    return 'Non hai il permesso di caricare file in questa cartella. Nessun file è stato caricato.'
  }
  const payload = failurePayload(failure)
  // DS-10: errore sul campo 'files' stesso = nessun archivio ricevuto, non un problema di dimensione
  if (payload && [payload.data, payload.errors].some(group => isPlainObject(group) && hasOwn(group, 'files'))) {
    return 'Il server non ha ricevuto nessun archivio: seleziona di nuovo i file .zip e riprova.'
  }
  // il classificatore di grz mostra solo il primo errore: qui servono tutti gli archivi rifiutati,
  // perché il server scarta l'intera richiesta e l'utente deve correggerli insieme
  const archives = payload && isPlainObject(payload.data) && Array.isArray(payload.data.archives)
    ? payload.data.archives.filter(item => typeof item === 'string' && item !== '')
    : []
  if (archives.length > 0) {
    return `Il server ha rifiutato il caricamento: ${archives.map(trimDots).join('; ')}. `
      + 'Nessun file è stato caricato.'
  }
  if (res.kind === 'server' && payload && payload.code === 0
    && typeof payload.message === 'string' && payload.message !== '') {
    return `${payload.message} Riprova più tardi.`
  }
  return res.message
}

// --------------------------------------------------------------- FTP ingest

const ingestRunningLabel = seconds => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const elapsed = total >= 60 ? `${Math.floor(total / 60)} min ${total % 60} s` : `${total} s`
  return `Importazione dei file dall'FTP in corso... (${elapsed})`
}

const REASON_TEXTS = {
  store_failed: 'errore durante il salvataggio',
  folder_failed: 'errore nella creazione della cartella (il contenuto resta sull\'FTP)',
}

const reasonText = reason => REASON_TEXTS[reason] || 'errore sconosciuto'

const statusBlock = (variant, kind, title) => ({
  variant,
  kind,
  title,
  lines: [],
  inProgress: [],
  moreInProgress: 0,
  failed: [],
  moreFailed: 0,
})

const describeIngestReport = payload => {
  try {
    if (!isPlainObject(payload) || payload.code !== 1) {
      const message = isPlainObject(payload) && typeof payload.message === 'string' && payload.message !== ''
        ? `: ${payload.message}` : ''
      return statusBlock('danger', 'report', `Importazione dall'FTP non riuscita${message}`)
    }

    const data = isPlainObject(payload.data) ? payload.data : {}
    const report = isPlainObject(data.report) ? data.report : null
    const legacyImported = Array.isArray(data.files) ? data.files.length : 0
    const imported = report && typeof report.imported === 'number' ? report.imported : legacyImported
    const folders = report && typeof report.folders_created === 'number' ? report.folders_created : 0
    const inProgress = report && Array.isArray(report.skipped_in_progress) ? report.skipped_in_progress : []
    const failed = report && Array.isArray(report.failed) ? report.failed : []
    const notRemoved = report && Array.isArray(report.not_removed) ? report.not_removed : []
    // solo un conteggio: un server senza la chiave (o un valore non valido) vale 0
    const uploading = report && typeof report.uploads_in_progress === 'number'
      && Number.isFinite(report.uploads_in_progress) && report.uploads_in_progress > 0
      ? Math.floor(report.uploads_in_progress) : 0

    let title
    if (imported > 0) {
      title = `${imported === 1 ? 'Importato' : 'Importati'} ${plural(imported, 'file', 'file')} dall'FTP nell'area di staging`
      if (folders > 0) {
        title += ` (${plural(folders, 'cartella creata', 'cartelle create')})`
      }
    } else if (folders > 0) {
      title = `Create ${plural(folders, 'cartella vuota', 'cartelle vuote')} dall'FTP nell'area di staging`
    } else if (inProgress.length > 0 || failed.length > 0 || uploading > 0) {
      title = 'Nessun file importato dall\'FTP'
    } else {
      title = 'Nessun nuovo file trovato sull\'FTP.'
    }

    const block = statusBlock('info', 'report', title)

    if (inProgress.length > 0) {
      block.lines.push(
        `${plural(inProgress.length, 'file ancora in caricamento sull\'FTP non è stato importato',
          'file ancora in caricamento sull\'FTP non sono stati importati')}: `
        + `${inProgress.length === 1 ? 'verrà importato' : 'verranno importati'} alla prossima apertura dello staging, `
        + 'a caricamento completato.',
      )
      block.inProgress = inProgress.slice(0, LIST_CAP).map(String)
      block.moreInProgress = Math.max(0, inProgress.length - LIST_CAP)
    }
    if (uploading > 0) {
      block.lines.push(uploading === 1
        ? '1 upload ancora in corso sull\'FTP: verrà importato quando completato.'
        : `${uploading} upload ancora in corso sull'FTP: verranno importati quando completati.`)
    }
    if (failed.length > 0) {
      block.lines.push(
        `${plural(failed.length, 'file non è stato importato', 'file non sono stati importati')} per un errore `
        + `e ${failed.length === 1 ? 'resta' : 'restano'} sull'FTP: riprova più tardi o contatta l'amministratore.`,
      )
      block.failed = failed.slice(0, LIST_CAP).map(item => {
        const entry = isPlainObject(item) ? item : {}
        return `${typeof entry.file === 'string' ? entry.file : '?'}: ${reasonText(entry.reason)}`
      })
      block.moreFailed = Math.max(0, failed.length - LIST_CAP)
    }

    if (notRemoved.length > 0) {
      const names = notRemoved.slice(0, LIST_CAP).map(String).join(', ')
      const more = notRemoved.length > LIST_CAP ? ` e altri ${notRemoved.length - LIST_CAP}` : ''
      block.lines.push(
        `${plural(notRemoved.length, 'file importato non è stato rimosso', 'file importati non sono stati rimossi')} `
        + `dall'FTP e al prossimo import ${notRemoved.length === 1 ? 'verrebbe duplicato' : 'verrebbero duplicati'}: `
        + `contatta l'amministratore (${names}${more}).`,
      )
    }

    if (failed.length > 0 && imported === 0) {
      block.variant = 'danger'
    } else if (failed.length > 0 || inProgress.length > 0 || uploading > 0 || notRemoved.length > 0) {
      block.variant = 'warning'
    } else if (imported > 0) {
      block.variant = 'success'
    }
    return block
  } catch (error) {
    return statusBlock('danger', 'report', 'Importazione dall\'FTP non riuscita')
  }
}

const retryAfterText = response => {
  const headers = response && isPlainObject(response.headers) ? response.headers : {}
  const seconds = Number(headers['retry-after'])
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '2 minuti'
  }
  if (seconds <= 60) {
    return 'meno di un minuto'
  }
  return `${Math.ceil(seconds / 60)} minuti`
}

// " (avviata alle HH:MM)" nell'ora locale del browser, solo se started_at e' una data valida
const startedAtText = response => {
  const body = response && isPlainObject(response.data) ? response.data : null
  const inner = body && isPlainObject(body.data) ? body.data : null
  const value = inner ? inner.started_at : null
  if (typeof value !== 'string' || value === '') {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const pad = number => (number < 10 ? `0${number}` : String(number))
  return ` (avviata alle ${pad(date.getHours())}:${pad(date.getMinutes())})`
}

const classifyIngestFailure = error => {
  try {
    const err = isPlainObject(error) ? error : {}
    const response = isPlainObject(err.response) ? err.response : null
    const status = response && typeof response.status === 'number' ? response.status : null

    if (!response) {
      const timedOut = err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT'
        || (typeof err.message === 'string' && /timeout/i.test(err.message))
      if (timedOut) {
        return statusBlock('warning', 'timeout',
          'La risposta del server non è arrivata in tempo: l\'importazione continua sul server e i file '
          + 'compariranno nell\'area di staging man mano. Aggiorna l\'elenco qui sotto tra qualche minuto.')
      }
      return statusBlock('danger', 'network',
        'Connessione interrotta durante l\'importazione dall\'FTP: i file non ancora importati restano sull\'FTP. '
        + 'Controlla la connessione e riprova.')
    }

    if (status === 429) {
      // Aprire lo staging due volte entro 2 minuti e' un uso normale, non un errore: variante 'info'
      return statusBlock('info', 'throttled',
        'L\'importazione dall\'FTP è già stata avviata da poco: si può avviare al massimo una volta ogni 2 minuti. '
        + `Riprova tra ${retryAfterText(response)}. Qui sotto trovi i file già presenti nell'area di staging.`)
    }
    if (status === 409) {
      // Il server esegue una sola ingestione per utente FTP alla volta: non e' un errore, quindi niente toast
      return statusBlock('info', 'already_running',
        `Un'importazione dall'FTP è già in corso${startedAtText(response)}: `
        + 'i file compariranno nell\'area di staging man mano; aggiorna l\'elenco tra qualche minuto.')
    }
    if (status === 401) {
      return statusBlock('danger', 'auth', 'Sessione scaduta: accedi di nuovo.')
    }
    if (status === 403) {
      return statusBlock('danger', 'rejected', 'Permesso negato: non puoi importare file dall\'FTP.')
    }
    if ([408, 502, 503, 504].includes(status)) {
      return statusBlock('warning', 'gateway',
        `Il server non ha risposto in tempo (errore HTTP ${status}). `
        + 'L\'importazione potrebbe essere ancora in corso: tra qualche minuto aggiorna l\'elenco qui sotto.')
    }
    const message = isPlainObject(response.data) ? response.data.message : null
    if (status >= 400 && status < 500 && typeof message === 'string' && message !== '') {
      return statusBlock('danger', 'rejected', `Importazione dall'FTP non riuscita: ${trimDots(message)}.`)
    }
    return statusBlock('danger', 'server',
      `Importazione dall'FTP non riuscita (errore HTTP ${status}). I file non importati restano sull'FTP: riprova più tardi.`)
  } catch (e) {
    return statusBlock('danger', 'server', 'Importazione dall\'FTP non riuscita. Riprova più tardi.')
  }
}

export default {
  TREE_PROCESSING_LABEL,
  INGEST_TIMEOUT_MS,
  treeSelectionProblem,
  treeUploadSummary,
  treeFailureMessage,
  ingestRunningLabel,
  describeIngestReport,
  classifyIngestFailure,
}
